import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Retrasa el valor hasta que deja de cambiar. Se usa en los buscadores para no
 * disparar una consulta por cada tecla.
 */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

/** `true` cuando la consulta de medios coincide. Reacciona a los cambios. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof matchMedia === 'function' ? matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** El punto de corte `lg` del sistema de diseño (1024 px). */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

/** Estado persistido en `localStorage`, tolerante a que esté bloqueado. */
export function usePersistedState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (value: T) => {
      setState(value);
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Sin persistencia el ajuste dura lo que dure la pestaña.
      }
    },
    [key],
  );

  return [state, set];
}

/**
 * Cronómetro que se refresca cada `intervalMs`. Sirve para los contadores en
 * vivo (SLA, «hace 3 min») sin montar un intervalo por cada componente hoja.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Atajo de teclado global. `combo` en minúsculas: `'k'` con `meta`/`ctrl`. */
export function useHotkey(
  combo: string,
  handler: () => void,
  options: { meta?: boolean; shift?: boolean } = {},
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== combo) return;
      if (options.meta && !(event.metaKey || event.ctrlKey)) return;
      if (!options.meta && (event.metaKey || event.ctrlKey)) return;
      if (options.shift && !event.shiftKey) return;
      event.preventDefault();
      handlerRef.current();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo, options.meta, options.shift]);
}
